/**
 * Mask IP address for Odoscope API compliance.
 * IPv4: Replace last octet with 0 (e.g., 192.168.1.100 → 192.168.1.0)
 * IPv6: Preserve first 48 bits (3 groups), zero out the rest (/48 mask)
 * @see https://learn.odoscope.cloud/knowledge-base/api-request-parameters/
 */
export const maskIpAddress = (ip: unknown): string | undefined => {
  if (!ip || typeof ip !== 'string' || ip.trim() === '') {
    return undefined;
  }

  const trimmedIp = ip.trim();

  // IPv4: simple pattern
  if (/^\d+\.\d+\.\d+\.\d+$/.test(trimmedIp)) {
    const parts = trimmedIp.split('.');
    parts[3] = '0';
    return parts.join('.');
  }

  // IPv6 handling
  if (trimmedIp.includes(':')) {
    // Basic IPv6 masking: preserve first 3 segments if possible, commonly /48 mask for privacy
    // or zero out the segments after colon-rich parts.
    // For compliance, zeroing out everything after the first 3 groups is a standard practice.
    const parts = trimmedIp.split(':');

    // If it's a very short address like ::1, we still want a masked representation
    if (trimmedIp === '::1') {
      return '127.0.0.1'; // Return as IPv4 localhost representation or keep ::1
    }

    if (parts.length >= 3) {
      // Ensure we always return 8 segments for consistency
      const maskedParts = [...parts.slice(0, 3), '0', '0', '0', '0', '0'];
      return maskedParts.join(':');
    }

    return undefined;
  }

  return undefined; // Invalid format
};
